import React, { useContext, useEffect, useRef, useState } from "react";
import "./AdvanceDashboard.css";
import Header from "../../Header/Header";
import { useLocation, useNavigate } from "react-router-dom";
import Dashboard_LineChart from "./Dashboard_LineChart/Dashboard_LineChart";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Context } from "../../../utils/context";
import { RoleId } from "../../../utils/common";
import { deleteData } from "../../../utils/api";
import { IconAction } from "../../common/TableUI/TableUI";
import { NOTIFICATIONS_SECTION_ID } from "./dashboardSections";

/* ------------------------------------------------------------------ */
/* Small helper: animates a number from 0 -> value (keeps prefix/suffix) */
/* Falls back to the raw value if it is not numeric.                     */
/* ------------------------------------------------------------------ */
function CountUp({ value, duration = 1300 }) {
  const [display, setDisplay] = useState(value ?? 0);

  useEffect(() => {
    if (value === undefined || value === null || value === "") {
      setDisplay(0);
      return undefined;
    }


    

    const raw = String(value);
    const target = parseFloat(raw.replace(/[^0-9.-]/g, ""));
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (Number.isNaN(target) || reduceMotion) {
      setDisplay(value);
      return undefined;
    }

    const prefix = (raw.match(/^[^0-9-]*/) || [""])[0];
    const suffix = (raw.match(/[^0-9]*$/) || [""])[0];
    const decimals = raw.includes(".")
      ? raw.split(".")[1].replace(/[^0-9]/g, "").length
      : 0;
    const useGrouping = raw.includes(",");

    let frame;
    let start;

    const tick = (now) => {
      if (!start) start = now;
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4); // easeOutQuart
      const current = target * eased;
      setDisplay(
        `${prefix}${current.toLocaleString("en-US", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
          useGrouping,
        })}${suffix}`,
      );
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{display}</>;
}

/* ------------------------------------------------------------------ */
/* Presentation helpers                                                 */
/* ------------------------------------------------------------------ */
const EASE = [0.22, 1, 0.36, 1];

const fadeRise = {
  hidden: { opacity: 0, y: 14 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE, delay: i * 0.06 },
  }),
};

const hasValue = (v) => v !== undefined && v !== null && v !== "";

/* Enter / Space activate clickable cards, same as a mouse click. */
const clickableProps = (onClick) =>
  onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

/**
 * Summary card.
 * - value: real API value (animated with CountUp unless `animate` is false)
 * - linkText: shown instead of a number for navigation-only cards
 */
function StatCard({
  title,
  caption,
  value,
  icon,
  tone = "primary",
  onClick,
  index = 0,
  loading = false,
  animate = true,
  linkText,
}) {
  return (
    <motion.div
      className={`adb-stat adb-stat--${tone} ${onClick ? "is-clickable" : ""}`}
      variants={fadeRise}
      initial="hidden"
      animate="visible"
      custom={index}
      {...clickableProps(onClick)}
    >
      <div className="adb-stat__top">
        <span className="adb-stat__icon" aria-hidden="true">
          {icon}
        </span>
        <h3 className="adb-stat__title">{title}</h3>
      </div>

      <div className="adb-stat__body">
        {loading ? (
          <span
            className="adb-skeleton adb-skeleton--value"
            aria-hidden="true"
          />
        ) : linkText ? (
          <span className="adb-stat__link">
            {linkText}
            <ArrowForwardIcon className="adb-stat__link-icon" />
          </span>
        ) : (
          <p className="adb-stat__value">
            {!hasValue(value) ? (
              "—"
            ) : animate ? (
              <CountUp value={value} />
            ) : (
              value
            )}
          </p>
        )}
        {caption && <p className="adb-stat__caption">{caption}</p>}
      </div>
    </motion.div>
  );
}

function AdvanceDashboard() {
  const [value, setValue] = useState(2);
  const navigate = useNavigate();
  const location = useLocation();
  const { getData, usertype } = useContext(Context);

  const [result, setResult] = useState({});
  const [pieData, setPieData] = useState();
  const [avgRating, setAvgRating] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [chartData, setChartData] = useState({});

  /* UI-only request states (do not change what is requested or stored) */
  const [statsLoading, setStatsLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(false);

  const [doctor, setDoctor] = useState({});
  const getDataAll = async () => {
    try {
      const result = await getData("/admin/dashboard/dashboard-data");
      setStatusCounts(result?.data);
    } finally {
      setStatsLoading(false);
    }
  };
  const getDataDoctor = async () => {
    const result = await getData("/admin/dashboard/doctor-wallet");
    setDoctor(result?.data);
  };

  const getChartData = async () => {
    try {
      const result = await getData("/admin/dashboard/sales-chart");
      setChartData(result.data);
      setChartError(false);
    } catch (error) {
      console.error("Sales chart error:", error);
      setChartError(true);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    getDataAll();
    getChartData();
  }, []);

  const [user, setUser] = useState({});

  const GetUser = async () => {
    const response = await getData(`/common/auth/usersingleget`);
    if (response?.success) {
      await setUser(response?.data);
    }
  };

  const [data, setData] = useState([]);
  const [removingId, setRemovingId] = useState(null);

  /* ---------- notification pagination ---------- */
  // 5 cards per page so the card fits beside the sales / orders column
  // without a scrollbar. Works with both:
  //  - an API that returns every notification at once (pages are cut here), and
  //  - a paginated API (Laravel style: data.data + total / last_page / per_page),
  //    where each page click requests the next page from the server.
  const NOTIFICATIONS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [serverMeta, setServerMeta] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState(false);
  const notifSectionRef = useRef(null);

  const allNotifications = data || [];
  const serverPaged = !!serverMeta && serverMeta.lastPage > 1;
  const perPage = serverPaged
    ? serverMeta.perPage || NOTIFICATIONS_PER_PAGE
    : NOTIFICATIONS_PER_PAGE;

  const totalNotifications = serverPaged
    ? serverMeta.total
    : allNotifications.length;
  const totalPages = serverPaged
    ? serverMeta.lastPage
    : Math.max(1, Math.ceil(totalNotifications / perPage));
  const safePage = Math.min(currentPage, totalPages);

  const visibleNotifications = serverPaged
    ? allNotifications
    : allNotifications.slice((safePage - 1) * perPage, safePage * perPage);

  const rangeStart =
    totalNotifications === 0 ? 0 : (safePage - 1) * perPage + 1;
  const rangeEnd = Math.min(safePage * perPage, totalNotifications);

  // window of up to 5 page buttons around the current page
  const pageStart = Math.max(1, Math.min(safePage - 2, totalPages - 4));
  const pageEnd = Math.min(totalPages, pageStart + 4);
  const pageNumbers = [];
  for (let p = pageStart; p <= pageEnd; p++) pageNumbers.push(p);

  // per_page is a hint for paginated backends; an API that ignores it and
  // returns the full list is still paged client-side below.
  const notificationUrl = (page) =>
    `/admin/notification?page=${page}&per_page=${NOTIFICATIONS_PER_PAGE}`;

  const getnotification = async (page = 1) => {
    setNotifLoading(true);
    try {
      const response = await getData(notificationUrl(page));

      if (response?.success) {
        setNotifError(false);
        const payload = response?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
        await setData(list);

        if (payload && (payload.last_page || payload.total)) {
          const items = payload.data?.length || 0;
          const pp = payload.per_page || items || NOTIFICATIONS_PER_PAGE;
          setServerMeta({
            total: payload.total ?? items,
            lastPage:
              payload.last_page ??
              Math.max(1, Math.ceil((payload.total ?? items) / pp)),
            perPage: pp,
          });
        } else {
          setServerMeta(null);
        }
      } else {
        setNotifError(true);
      }
    } catch (error) {
      console.error("Notification error:", error);
      setNotifError(true);
    } finally {
      setNotifLoading(false);
    }
  };

  // after a page change bring the top of the card back into view
  const revealNotifications = () => {
    if (notifSectionRef.current) {
      notifSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const goToPage = async (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    if (serverPaged) {
      await getnotification(page);
    }
    revealNotifications();
  };

  const deletenotification = async (id) => {
    if (!id) return;
    setRemovingId(id);
    setTimeout(async () => {
      try {
        const response = await deleteData(`/admin/notification/${id}`);
        if (response?.success) {
          setData((prev) => prev.filter((item) => item.id !== id));
        }
      } catch (error) {
        console.error("Delete notification error:", error);
      } finally {
        setRemovingId(null);
      }
    }, 400);
  };

  useEffect(() => {
    GetUser();
    getDataDoctor();
    getnotification();
  }, []);

  /* Header bell: navigates here with state.scrollTo = NOTIFICATIONS_SECTION_ID.
     The card only renders once the user (admin) has loaded, so this waits for
     the ref. The request is consumed by replacing the history state, which
     stops a second scroll on re-render or page refresh. */
  const stickyHeaderRef = useRef(null);
  const scrollRequest = location.state?.scrollTo;
  useEffect(() => {
    if (scrollRequest !== NOTIFICATIONS_SECTION_ID) return;
    const section = notifSectionRef.current;
    if (!section) return;

    // keep the card clear of the sticky page header
    const headerHeight = stickyHeaderRef.current?.offsetHeight || 0;
    section.style.scrollMarginTop = `${headerHeight + 16}px`;
    section.scrollIntoView({ behavior: "smooth", block: "start" });

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    // user?.role_id: re-run once the admin layout (and the card) renders
  }, [location.key, location.pathname, location.search, navigate, scrollRequest, user?.role_id]);

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  /* Today's order breakdown — same fields and order as before */
  const orderStatuses = [
    // { label: "New Orders", value: statusCounts?.Pending_orders, tone: "info" },
    {
      label: "Processing Orders",
      value: statusCounts?.Processing_orders,
      tone: "warning",
    },
    { label: "Preparing Orders", value: statusCounts?.Preparing, tone: "info" },
    {
      label: "Under Quality Check",
      value: statusCounts?.OrderUnderQualityCheck,
      tone: "accent",
    },
    {
      label: "Delivered Orders",
      value: statusCounts?.Delivered_orders,
      tone: "success",
    },
    {
      label: "Cancelled Orders",
      value: statusCounts?.Cancelled_orders,
      tone: "muted",
    },
    {
      label: "Rejected Orders",
      value: statusCounts?.Rejected_orders,
      tone: "danger",
    },
  ];

  return (
    <MotionConfig reducedMotion="user">
      <div>
        <div className="main-advancedashboard">
          <section className="AdvanceDashboard" ref={stickyHeaderRef}>
            <div className="AdvanceDashboardTabs">
              <Header title={"Dashboard"} link={"/masters/social_links"} />
              <div className=""></div>
            </div>
          </section>

          {user.role_id === RoleId.Doctor && (
            <div className="Main-Section-Advanced-Dashboard adb">
              <div className="adb-intro">
                <h2 className="adb-intro__title">Overview</h2>
              </div>

              <div className="adb-stats adb-stats--doctor">
                <StatCard
                  index={0}
                  title="Wallet Amount"
                  caption="Current balance"
                  value={doctor?.wallet?.amount}
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  tone="primary"
                />
                <StatCard
                  index={1}
                  title="Appointment"
                  caption="Total appointments"
                  value={doctor?.appointment?.length}
                  icon={<EventAvailableOutlinedIcon />}
                  tone="gold"
                  onClick={() => {
                    navigate("/subscription/appointment");
                  }}
                />
                <StatCard
                  index={2}
                  title="Doctor Code"
                  caption="Share with customers"
                  value={doctor?.doctor?.doctor_code}
                  animate={false}
                  icon={<BadgeOutlinedIcon />}
                  tone="secondary"
                />
              </div>
            </div>
          )}

          {/* ============================= ADMIN ============================ */}
          {user.role_id === RoleId.Admin && (
            <div className="Main-Section-Advanced-Dashboard adb">
              <div className="adb-intro">
                <h2 className="adb-intro__title">Overview</h2>
              </div>

              {/* ---------------- summary cards ---------------- */}
              <div className="adb-stats">
                <StatCard
                  index={0}
                  title="Products"
                  caption="Total Products"
                  value={statusCounts?.total_products}
                  loading={statsLoading}
                  icon={<Inventory2OutlinedIcon />}
                  tone="primary"
                  onClick={() => {
                    user?.Store_Detail?.s_category?.is_restaurant_flow
                      ? navigate("/Foods/:id")
                      : navigate("/products");
                  }}
                />
                <StatCard
                  index={1}
                  title="Stock"
                  caption="Product Stock"
                  linkText="Manage stock"
                  icon={<WarehouseOutlinedIcon />}
                  tone="secondary"
                  onClick={() => {
                    navigate("/product/product-stock");
                  }}
                />
                <StatCard
                  index={2}
                  title="Customer Feedback"
                  caption="Total Messages"
                  value={statusCounts?.total_reviews || 0}
                  loading={statsLoading}
                  icon={<RateReviewOutlinedIcon />}
                  tone="gold"
                  onClick={() => navigate("/product/ratings/:id")}
                />
                <StatCard
                  index={3}
                  title="Today's Payment"
                  caption="Total received today"
                  value={statusCounts?.todays_total}
                  loading={statsLoading}
                  icon={<PaymentsOutlinedIcon />}
                  tone="success"
                />
                <StatCard
                  index={4}
                  title="Total Customer"
                  caption="Registered customers"
                  value={statusCounts?.TotalCustomer}
                  loading={statsLoading}
                  icon={<PeopleAltOutlinedIcon />}
                  tone="primary"
                  onClick={() => navigate("/customers")}
                />

                {/* Subscription cards (disabled, kept for reference):
                <StatCard title="Total Subscription" value={statusCounts?.TotalSubscription}
                  icon={<EventAvailableOutlinedIcon />} onClick={() => navigate("/subscriptions")} />
                <StatCard title="Total Active Subscription" value={statusCounts?.TotalActiveSubscription}
                  icon={<AccountBalanceWalletOutlinedIcon />} onClick={() => navigate("/subscriptions")} />
                <StatCard title="Total Doctor Subscription" value={statusCounts?.TotaldoctorSubscription}
                  icon={<AccountBalanceWalletOutlinedIcon />} onClick={() => navigate("/doctor-subscriptions")} />
                */}
              </div>

              {/* ---------------- main grid ---------------- */}
              <div className="adb-grid">
                <div className="adb-grid__main">
                  {/* ============ SALES ============ */}
                  <motion.section
                    className="adb-panel adb-sales"
                    variants={fadeRise}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                    aria-labelledby="adb-sales-title"
                  >
                    <header className="adb-panel__header">
                      <div className="adb-panel__heading">
                        <span className="adb-panel__icon" aria-hidden="true">
                          <TrendingUpIcon />
                        </span>
                        <div>
                          <h3 className="adb-panel__title" id="adb-sales-title">
                            Sales
                          </h3>
                          <p className="adb-panel__subtitle">This month</p>
                        </div>
                      </div>

                      <div className="adb-sales__total">
                        {chartLoading ? (
                          <span
                            className="adb-skeleton adb-skeleton--total"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="adb-sales__total-value">
                            {hasValue(chartData?.total) ? (
                              <CountUp value={chartData?.total} />
                            ) : (
                              "—"
                            )}
                          </span>
                        )}
                        <span className="adb-sales__total-label">
                          Total this month
                        </span>
                      </div>
                    </header>

                    <div className="adb-sales__chart">
                      <Dashboard_LineChart
                        chartData={chartData?.data}
                        loading={chartLoading}
                        error={chartError}
                      />
                    </div>
                  </motion.section>

                  {/* ============ TODAY'S ORDERS ============ */}
                  <motion.section
                    className="adb-panel adb-orders is-clickable"
                    variants={fadeRise}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    aria-labelledby="adb-orders-title"
                    {...clickableProps(() => navigate("/orders/all-orders"))}
                  >
                    <header className="adb-panel__header">
                      <div className="adb-panel__heading">
                        <span className="adb-panel__icon" aria-hidden="true">
                          <ShoppingCartOutlinedIcon />
                        </span>
                        <div>
                          <h3
                            className="adb-panel__title"
                            id="adb-orders-title"
                          >
                            Today's Orders
                          </h3>
                          <p className="adb-panel__subtitle">Today Count</p>
                        </div>
                      </div>

                      <div className="adb-orders__count">
                        {statsLoading ? (
                          <span
                            className="adb-skeleton adb-skeleton--total"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="adb-orders__count-value">
                            {hasValue(statusCounts?.total_Orders) ? (
                              <CountUp value={statusCounts?.total_Orders} />
                            ) : (
                              "—"
                            )}
                          </span>
                        )}
                        <span className="adb-orders__view">
                          View orders
                          <ArrowForwardIcon className="adb-orders__view-icon" />
                        </span>
                      </div>
                    </header>

                    <ul className="adb-orders__list">
                      {orderStatuses.map((s) => (
                        <li className="adb-orders__row" key={s.label}>
                          <span
                            className={`adb-dot adb-dot--${s.tone}`}
                            aria-hidden="true"
                          />
                          <span className="adb-orders__label">{s.label}</span>
                          <span
                            className={`adb-orders__value adb-orders__value--${s.tone}`}
                          >
                            {statsLoading ? (
                              <span
                                className="adb-skeleton adb-skeleton--pill"
                                aria-hidden="true"
                              />
                            ) : hasValue(s.value) ? (
                              s.value
                            ) : (
                              "—"
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.section>
                </div>

                {/* ============ NOTIFICATIONS ============ */}
                <motion.section
                  id={NOTIFICATIONS_SECTION_ID}
                  ref={notifSectionRef}
                  className="adb-panel adb-notif"
                  variants={fadeRise}
                  initial="hidden"
                  animate="visible"
                  custom={4}
                  aria-labelledby="adb-notif-title"
                >
                  <header className="adb-panel__header adb-notif__header">
                    <div className="adb-panel__heading">
                      <span className="adb-panel__icon" aria-hidden="true">
                        <NotificationsNoneOutlinedIcon />
                      </span>
                      <div>
                        <h3 className="adb-panel__title" id="adb-notif-title">
                          Notifications
                        </h3>
                        <p className="adb-panel__subtitle">Latest updates</p>
                      </div>
                    </div>
                    {totalNotifications > 0 && (
                      <span
                        className="adb-notif__count"
                        aria-label={`${totalNotifications} notifications`}
                      >
                        {totalNotifications}
                      </span>
                    )}
                  </header>

                  <div
                    className={`adb-notif__list notification_list ${
                      notifLoading ? "is-loading" : ""
                    }`}
                    aria-busy={notifLoading}
                    aria-live="polite"
                  >
                    {visibleNotifications.length > 0 ? (
                      <motion.ul
                        className="adb-notif__items"
                        key={`page-${safePage}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.25 }}
                      >
                        <AnimatePresence initial={false}>
                          {visibleNotifications.map((item, index) => (
                            <motion.li
                              layout="position"
                              className={`adb-notif-item ${
                                removingId === item.id ? "is-removing" : ""
                              }`}
                              key={item.id}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: {
                                  duration: 0.3,
                                  ease: EASE,
                                  delay: Math.min(index, 8) * 0.035,
                                },
                              }}
                              exit={{
                                opacity: 0,
                                x: 24,
                                transition: { duration: 0.25, ease: "easeIn" },
                              }}
                            >
                              <span
                                className="adb-notif-item__icon"
                                aria-hidden="true"
                              >
                                <NotificationsActiveOutlinedIcon />
                              </span>
                              <div className="adb-notif-item__body">
                                <h4 className="adb-notif-item__title notification_name">
                                  {item?.name}
                                </h4>
                                <p className="adb-notif-item__message notification_subname">
                                  {item?.message}
                                </p>
                              </div>
                            </motion.li>
                          ))}
                        </AnimatePresence>
                      </motion.ul>
                    ) : notifLoading ? (
                      <ul className="adb-notif__items" aria-hidden="true">
                        {[0, 1, 2, 3].map((i) => (
                          <li
                            className="adb-notif-item adb-notif-item--skeleton"
                            key={i}
                          >
                            <span className="adb-skeleton adb-skeleton--avatar" />
                            <div className="adb-notif-item__body">
                              <span className="adb-skeleton adb-skeleton--line" />
                              <span className="adb-skeleton adb-skeleton--line short" />
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : notifError ? (
                      <div
                        className="no-notification adb-notif__empty"
                        role="alert"
                      >
                        <span
                          className="adb-notif__empty-icon"
                          aria-hidden="true"
                        >
                          <NotificationsNoneOutlinedIcon />
                        </span>
                        <p>Couldn't load notifications.</p>
                        <button
                          type="button"
                          className="adb-notif__viewall"
                          onClick={() => getnotification(safePage)}
                        >
                          Try again
                        </button>
                      </div>
                    ) : (
                      <div className="no-notification adb-notif__empty">
                        <span
                          className="adb-notif__empty-icon"
                          aria-hidden="true"
                        >
                          <NotificationsNoneOutlinedIcon />
                        </span>
                        <p>No notifications found.</p>
                      </div>
                    )}
                  </div>

                  {totalNotifications > 0 && (
                    <footer className="adb-notif__footer">
                      <span className="adb-notif__range">
                        {`${rangeStart}–${rangeEnd} of ${totalNotifications}`}
                        {totalPages > 1 && (
                          <span className="adb-notif__page">
                            Page {safePage} of {totalPages}
                          </span>
                        )}
                      </span>

                      {totalPages > 1 && (
                        <nav
                          className="adb-pager"
                          aria-label="Notification pages"
                        >
                          <IconAction
                            title="Previous page"
                            icon={<ChevronLeftIcon />}
                            onClick={() => goToPage(safePage - 1)}
                            disabled={safePage === 1 || notifLoading}
                          />

                          {pageNumbers.map((page) => (
                            <button
                              type="button"
                              key={page}
                              className={`adb-pager__btn ${
                                page === safePage ? "is-active" : ""
                              }`}
                              onClick={() => goToPage(page)}
                              aria-current={
                                page === safePage ? "page" : undefined
                              }
                              aria-label={`Page ${page}`}
                            >
                              {page}
                            </button>
                          ))}

                          <IconAction
                            title="Next page"
                            icon={<ChevronRightIcon />}
                            onClick={() => goToPage(safePage + 1)}
                            disabled={safePage === totalPages || notifLoading}
                          />
                        </nav>
                      )}
                    </footer>
                  )}
                </motion.section>
              </div>

              {/* Featured Offers / Feedback rating cards were disabled in the
                  previous version (they used `result` and `avgRating`); the
                  state is kept above so they can be restored as StatCards. */}
            </div>
          )}
        </div>
      </div>
    </MotionConfig>
  );
}

export default AdvanceDashboard;
